/**
 * A payment link for an amount somebody types.
 *
 * Jonathon, 2026-09-12, while answering the rest of the payment plan: "we need
 * the option to send a payment link for a manual amount to customers."
 *
 * Every other payment Roof Board asks for is one of the four on the schedule,
 * queued when the contract is signed and priced by the ladder. This is the one
 * that is not: a change order, half a balance a customer wants to split, a
 * repair billed after the fact. The rep types the figure and what it is for,
 * and gets a link back to text or email (ADR 0141).
 *
 * What it does **not** do is decide anything about the schedule. The request
 * holds no slot, no box is ticked when the link is made, and when the money
 * lands what it settles is worked out from the amount like any other credit —
 * which was Jonathon's answer when asked: work it out, and tick nothing if it
 * fits nothing.
 *
 * The card option adds the same 3% the customer's own page adds, and says so
 * before anybody sends anything.
 */

import {
  CARD_SURCHARGE_RATE,
  cents,
  chargeFor,
  formatMoney,
  toCents,
} from "@roof-hawk/core";
import { useState } from "react";

import { ErrorNote, Modal } from "../../components/ui";
import { t } from "../../i18n";
import { raisePaymentLink } from "../../lib/payments";

export function PaymentLinkModal({
  jobId,
  /** What the customer still owes, as the amount to start from. */
  suggestedCents,
  /** For the sentence on the confirmation. */
  customerName,
  /**
   * Amounts this customer already has a live link for.
   *
   * Only to warn with. A link for the same amount as an unpaid deposit means
   * two live invoices for one payment, and a customer can pay both (Josh,
   * 2026-09-13).
   */
  liveLinkCents = [],
  onClose,
  onMade,
}: {
  jobId: string;
  suggestedCents?: number | null;
  customerName?: string | null;
  liveLinkCents?: readonly number[];
  onClose: () => void;
  onMade?: () => Promise<void> | void;
}) {
  const [amount, setAmount] = useState(
    suggestedCents && suggestedCents > 0
      ? (suggestedCents / 100).toFixed(2)
      : "",
  );
  const [purpose, setPurpose] = useState("");
  const [method, setMethod] = useState<"ach" | "card">("ach");
  const [busy, setBusy] = useState(false);
  /** Showing "you are about to bill X" — every link goes through this. */
  const [confirming, setConfirming] = useState(false);
  /**
   * The amount "Are you sure?" read back, taken when that screen opened.
   *
   * Sent as the confirmation, separately from the amount itself, so the
   * server's check compares what was asked for with what the person was
   * shown — not one variable with itself (round 4 of #235).
   */
  const [confirmedCents, setConfirmedCents] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [made, setMade] = useState<{ url: string; fee: number } | null>(null);
  const [copied, setCopied] = useState(false);

  const typed = toCents(amount);
  // The same 3% the customer's own page quotes, from the one function that
  // multiplies by the rate (CLAUDE.md rules 3 and 5 — this screen did the
  // arithmetic itself until 2026-09-13, so a change to the rate in core would
  // have left it promising 3% for ever).
  const { total, surcharge: fee } = chargeFor(
    method === "card" ? "card" : "ach",
    typed,
  );
  const looksLikeAScheduledPayment = liveLinkCents.some(
    (amount) => Math.round(Number(amount)) === Math.round(Number(typed)),
  );

  /*
   * Every link is read back before it exists.
   *
   * Jonathon, 2026-09-13, asked whether a big amount should stop and ask:
   * "it should always ask are you sure". So `Make the link` never raises
   * anything — it shows what is about to be billed, to whom, and the Send
   * button on that screen is the only thing that reaches Mercury. `150000`
   * typed for $1,500.00 is caught by reading the sentence.
   */
  const check = () => {
    if (!(Number(typed) > 0)) {
      setError(t("Type the amount to ask for."));
      return;
    }
    if (!/^\s*\$?\s*\d{1,3}(,?\d{3})*(\.\d{1,2})?\s*$/.test(amount)) {
      setError(t("Type the amount in dollars and cents, like 1500.00."));
      return;
    }
    if (purpose.trim() === "") {
      setError(t("Say what it is for — the customer reads it on the invoice."));
      return;
    }
    setError(null);
    setConfirmedCents(Number(typed));
    setConfirming(true);
  };

  const make = async () => {
    if (confirmedCents == null) return;
    setBusy(true);
    setError(null);
    try {
      const result = await raisePaymentLink({
        jobId,
        amountCents: Number(typed),
        confirmedAmountCents: confirmedCents,
        purpose: purpose.trim(),
        method,
      });
      setMade({ url: result.paymentUrl, fee: result.surchargeCents });
      await onMade?.();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause));
    } finally {
      setBusy(false);
    }
  };

  if (made) {
    return (
      <Modal
        title={t("The link is ready")}
        subtitle={t("Nothing has been sent. Copy it into a text or an email.")}
        onClose={onClose}
        actions={
          <button type="button" className="btn-primary" onClick={onClose}>
            {t("Done")}
          </button>
        }
      >
        <p className="na-hint">
          {formatMoney(cents(Number(typed)))}
          {made.fee > 0
            ? ` ${t("plus {fee} card fee", { fee: formatMoney(cents(made.fee)) })}`
            : ""}{" "}
          · {purpose.trim()}
        </p>
        <div className="field">
          <label htmlFor="pay-link">{t("Payment link")}</label>
          <input id="pay-link" readOnly value={made.url} />
        </div>
        <button
          type="button"
          className="btn-small"
          onClick={() => {
            void navigator.clipboard?.writeText(made.url);
            setCopied(true);
          }}
        >
          {copied ? t("Copied") : t("Copy the link")}
        </button>
      </Modal>
    );
  }

  if (confirming) {
    return (
      <Modal
        title={t("Are you sure?")}
        subtitle={t(
          "Nothing has been billed yet. Read the amount back before the invoice exists.",
        )}
        onClose={onClose}
        actions={
          <>
            <button
              type="button"
              className="btn-ghost"
              disabled={busy}
              onClick={() => setConfirming(false)}
            >
              {t("Go back and change it")}
            </button>
            <button
              type="button"
              className="btn-primary"
              disabled={busy}
              onClick={() => void make()}
            >
              {busy ? t("Making the link…") : t("Yes, make the link")}
            </button>
          </>
        }
      >
        <ErrorNote error={error} />
        <p>
          {customerName ? (
            <>
              {t("You are about to bill")} <b>{customerName}</b>{" "}
            </>
          ) : (
            `${t("You are about to bill")} `
          )}
          <b>{formatMoney(cents(Number(total)))}</b>
          {fee > 0 ? (
            <>
              {" "}
              {t("— {amount} plus {fee} card fee", {
                amount: formatMoney(cents(Number(typed))),
                fee: formatMoney(cents(Number(fee))),
              })}
            </>
          ) : null}
          .
        </p>
        <p className="na-hint">
          {t("For: {purpose}", { purpose: purpose.trim() })}
        </p>
        {looksLikeAScheduledPayment && (
          <div className="na-hint" role="status">
            {t(
              "That is the same amount as a payment this customer already has a link for. If they are meant to pay that one, send them that link instead — two live invoices for the same money is how somebody pays twice.",
            )}
          </div>
        )}
      </Modal>
    );
  }

  return (
    <Modal
      title={t("Payment link for another amount")}
      subtitle={t(
        "For money the schedule knows nothing about — a change order, part of a balance, a repair.",
      )}
      onClose={onClose}
      actions={
        <>
          <button type="button" className="btn-ghost" onClick={onClose}>
            {t("Cancel")}
          </button>
          <button
            type="button"
            className="btn-primary"
            disabled={busy}
            onClick={check}
          >
            {t("Make the link")}
          </button>
        </>
      }
    >
      <ErrorNote error={error} />
      <div className="field">
        <label htmlFor="link-amount">{t("Amount")}</label>
        <input
          id="link-amount"
          inputMode="decimal"
          value={amount}
          onChange={(event) => setAmount(event.target.value)}
        />
      </div>
      <div className="field">
        <label htmlFor="link-purpose">{t("What it is for")}</label>
        <input
          id="link-purpose"
          value={purpose}
          placeholder={t("Chimney flashing change order")}
          onChange={(event) => setPurpose(event.target.value)}
        />
      </div>
      <div className="field">
        <label htmlFor="link-method">{t("How they will pay")}</label>
        <select
          id="link-method"
          value={method}
          onChange={(event) =>
            setMethod(event.target.value === "card" ? "card" : "ach")
          }
        >
          <option value="ach">{t("Bank transfer — no fee")}</option>
          <option value="card">
            {/* The rate from core, not a number typed here: if it ever moves,
                this stops promising 3% (CLAUDE.md rule 5). */}
            {t("Credit card — {pct}% added", {
              pct: Math.round(CARD_SURCHARGE_RATE * 100),
            })}
          </option>
        </select>
      </div>

      <div className="na-hint">
        {fee > 0
          ? t(
              "They will be asked for {total} — {amount} plus {fee} card fee.",
              {
                total: formatMoney(cents(Number(total))),
                amount: formatMoney(cents(Number(typed))),
                fee: formatMoney(cents(Number(fee))),
              },
            )
          : t("They will be asked for exactly this amount.")}{" "}
        {t(
          "This payment is on no schedule, so when the money lands Roof Board works out from the amount whether it settles the deposit or the balance, and ticks nothing if it settles neither.",
        )}
      </div>
    </Modal>
  );
}
